import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Avatar,
  Button,
} from '@mui/material';
import MDButton from 'components/MDButton';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import { useFormik } from 'formik';

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const validationBusinessCategorySchema = yup.object().shape({
  business_category: yup
    .string()
    .required('Business Category is required.')
    .min(3, 'Business Category must be at least 3 characters.')
    .max(50, 'Business Category must not exceed 50 characters.')
    .matches(
      /^[A-Za-z\s]+$/,
      'Business Category must only contain letters and spaces.'
    ),
});

export default function BusinessCategoryEditModel({ open, handleClose, editInput }) {
  const [duplicateError, setDuplicateError] = useState('');
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [preview, setPreview] = useState(null);
  const [isImageDirty, setIsImageDirty] = useState(false);

  useEffect(() => {
    if (editInput?.image_url) {
      setPreview(editInput.image_url);
    } else {
      setPreview(null);
    }
    setImage(null);
    setImageError('');
    setDuplicateError('');
    setIsImageDirty(false);
  }, [editInput, open]);

  const initialValues = {
    business_category: editInput?.business_category || '',
    business_category_id: editInput?.business_category_id || '',
  };

  const checkDuplicate = async (value) => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === editInput?.business_category || !/^[A-Za-z\s]+$/.test(trimmed)) {
      setDuplicateError('');
      return;
    }

    try {
      const res = await api.post('/vendor_management/business_category/check_duplicate', {
        business_category: trimmed,
      });

      if (res.data.status === 1 && res.data.business_category_id !== editInput.business_category_id) {
        setDuplicateError('Business Category already exists');
      } else {
        setDuplicateError('');
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
      setDuplicateError('Error checking duplicate');
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationBusinessCategorySchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await formik.validateForm();

      if (!formik.isValid || Boolean(duplicateError) || Boolean(imageError)) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append('business_category', values.business_category.trim());

        if (image) {
          formData.append('image', image);
        }

        const response = await api.put(
          `/vendor_management/business_category/${values.business_category_id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.status === 1) {
          Swal.fire({
            icon: 'success',
            title: 'Business Category updated successfully!',
            timer: 3000,
            showConfirmButton: false,
          });

          formik.resetForm();
          setImage(null);
          setPreview(null);
          setImageError('');
          setDuplicateError('');
          setIsImageDirty(false);
          handleClose();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Failed',
            text: response.data.msg,
          });
        }
      } catch (error) {
        console.error('Error updating business category:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update business category.',
        });
      }
    },
  });

  const handleImageChangeAndDirty = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setImageError('Image size must be less than 2MB.');
        setImage(null);
        setPreview(editInput?.image_url || null);
        setIsImageDirty(false);
      } else {
        setImage(file);
        setImageError('');
        setPreview(URL.createObjectURL(file));
        setIsImageDirty(true);
      }
    } else {
      setImage(null);
      setImageError('');
      setPreview(editInput?.image_url || null);
      setIsImageDirty(false);
    }
  };

  const handleCancel = () => {
    formik.resetForm();
    formik.setErrors({});
    formik.setTouched({});
    setImage(null);
    setPreview(editInput?.image_url || null);
    setImageError('');
    setDuplicateError('');
    setIsImageDirty(false);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="md">
      <DialogTitle>Edit Business Category</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Business Category"
                name="business_category"
                value={formik.values.business_category}
                onChange={async (e) => {
                  formik.handleChange(e);
                  formik.setFieldTouched('business_category', true, false);
                  await formik.validateField('business_category');

                  if (validationBusinessCategorySchema.fields.business_category.isValidSync(e.target.value)) {
                    checkDuplicate(e.target.value);
                  } else {
                    setDuplicateError('');
                  }
                }}
                onBlur={formik.handleBlur}
                fullWidth
                margin="normal"
                error={
                  (formik.touched.business_category && Boolean(formik.errors.business_category)) ||
                  Boolean(duplicateError)
                }
                helperText={
                  (formik.touched.business_category && formik.errors.business_category) ||
                  duplicateError
                }
              />
            </Grid>

            <Grid item xs={12}>
              <MDButton
                variant="outlined"
                color="info"
                component="label"
                fullWidth
                sx={{ justifyContent: 'center' }}
              >
                Upload Image (Max 2MB)
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleImageChangeAndDirty}
                  onClick={(event) => { event.target.value = null; }}
                />
              </MDButton>

              {preview && (
                <Avatar
                  src={preview}
                  variant="rounded"
                  sx={{ mt: 2, width: 100, height: 100 }}
                />
              )}

              {imageError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {imageError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <MDButton
            type="submit"
            color="success"
            disabled={!formik.isValid || (!formik.dirty && !isImageDirty) || Boolean(duplicateError) || Boolean(imageError)}
          >
            Save
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}